<template>
  <!--
    v-show, nao v-if. Duas razoes, as duas obrigatorias:
      - o host das particulas e adotado uma unica vez no mounted; com v-if o
        container sumiria do DOM a cada transicao e levaria o canvas junto;
      - display:none e justamente o freio: o IntersectionObserver interno do
        MainParticlesComponent para de renderizar quando o container nao
        intersecta, entao a GPU so trabalha durante a transicao.
  -->
  <transition name="veil-fade">
    <div
      v-show="coreStore.navigating"
      class="veil"
    >
      <div class="veil__box">
        <div ref="particles" class="veil__particles"></div>
        <div class="veil__card">
          <v-icon v-if="icon" :size="46" color="#ffbf00">{{ icon }}</v-icon>
          <div class="veil__label">{{ $t('extras.opening') }}</div>
          <div class="veil__app">{{ name }}</div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapStores } from "pinia";
import { useCoreStore } from "@/store/coreStore";

export default {
  name: "RouteTransitionOverlay",
  computed: {
    ...mapStores(useCoreStore),
    // navigatingApp fica null durante a saida do veu (a rota ja trocou), entao
    // ler direto no template piscaria o nome para vazio antes do fade acabar.
    icon() {
      return this.coreStore.navigatingApp?.icon || null;
    },
    name() {
      return this.coreStore.navigatingApp?.name || "";
    },
  },
  watch: {
    "coreStore.navigating"(ligado) {
      if (!ligado) return;
      // O canvas foi dimensionado no boot, quando o host ainda estava solto no
      // body, e desde entao viveu sob display:none. Sem este resize ele volta
      // com o tamanho errado - ou 0x0, se a janela mudou nesse meio tempo.
      // nextTick espera o display virar; o rAF espera o layout fechar.
      this.$nextTick(() =>
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event("resize"));
          this.recentralizar();
        })
      );
    },
  },
  mounted() {
    // Adota o host criado no index.html (ver o comentario la). Ele ja foi
    // inicializado pelo modulo; aqui so muda de lugar no DOM, o que preserva o
    // contexto WebGL. Acontece uma vez na vida do app.
    const host = document.getElementById("veil-particles");
    if (!host || !this.$refs.particles) return;

    // A adocao e adiada de proposito. Construir a cena e barato; o caro e o
    // PRIMEIRO render, quando o WebGL compila e linka os shaders. Adotar na
    // hora esconderia o host (o veu esta em display:none) antes de qualquer
    // frame acontecer, e essa conta cairia no instante em que o veu aparece -
    // exatamente onde nao pode haver engasgo. Nesta janela o host renderiza
    // solto no body, invisivel: opacity 0, z-index -1, sem pointer-events.
    // Timer e nao contador de rAF porque rAF e estrangulado em aba de fundo, e
    // ali a adocao nunca aconteceria.
    this.aquecimento = setTimeout(() => {
      this.$refs.particles.appendChild(host);
      host.classList.add("is-adopted");
    }, 600);
  },
  beforeDestroy() {
    clearTimeout(this.aquecimento);
  },
  methods: {
    // A cena nunca e destruida - ela so pausa entre as transicoes - entao o anel
    // guarda a posicao para onde derivou na vez anterior e a proxima abertura
    // comecaria descentralizada. (0,0) e o centro do plano de particulas, que a
    // camera enquadra no centro do viewport.
    // `__particlesScene` so existe por causa do patch em MainParticlesComponent.js;
    // se ele sumir, isto vira no-op silencioso e o veu volta a comecar torto.
    recentralizar() {
      const cena = document.getElementById("veil-particles")?.__particlesScene;
      if (!cena?.particles) return;
      cena.particles.ringPos.set(0, 0);
      cena.particles.cursorPos.set(0, 0);
    },
  },
};
</script>

<style scoped>
/*
  A raiz continua cobrindo o viewport inteiro e transparente: e ela que impede
  clique na pagina que esta sendo trocada por baixo. Todo o visual - gradiente,
  desfoque, particulas e texto - mora na caixa interna.
*/
.veil {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  /* Acima de tudo, inclusive dos overlays do Vuetify, que param na casa dos 250. */
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  /* A caixa volta a ser a tela inteira - quem limita o efeito agora e o alcance
     do gradiente, nao o tamanho da caixa. */
  --veu-largura: 100%;
  --veu-altura: 100%;
  /* Ate onde o escurecimento chega antes de zerar, em % do raio - que com
     closest-side e meia-largura / meia-altura, ou seja, 100% = meio de cada
     borda da tela.
     O numero que importa e 141% (raiz de 2): e a distancia dos CANTOS nesse
     mesmo sistema, ja que o canto fica em (1,1). Abaixo disso os cantos ainda
     alcancam alpha zero e a curva da elipse aparece neles; acima, a tela
     inteira cai dentro da elipse e nada na tela chega a zero.
     189.75% deixa a elipse bem maior que a viewport: as bordas dela caem fora da
     tela, sobrando so o miolo do gradiente - ~0.82 no meio das bordas e ~0.56
     nos cantos.
     INDEPENDENTE de --veu-particulas-recuo - o tamanho da elipse e o do campo
     de particulas sao dois ajustes separados, nao ha razao para casa-los. */
  --veu-alcance: 189.75%;
  /* Quanto o campo de particulas encolhe em relacao a caixa, por lado. Encolhe
     o canvas de verdade (menos GPU), alem de garantir folga ate a borda.
     20% mantem o canvas em 60% do viewport - o mesmo tamanho absoluto que tinha
     com a caixa em 70% e recuo de 7%. */
  --veu-particulas-recuo: 20%;
}

.veil__box {
  position: relative;
  width: var(--veu-largura);
  height: var(--veu-altura);
  display: flex;
  align-items: center;
  justify-content: center;
  /* 98% no centro, onde mora o texto, dissolvendo a zero na borda.
     `closest-side` e o detalhe que importa: no default (`farthest-corner`) o
     raio 100% cai nos CANTOS, entao o meio de cada lado fica so a ~70% do raio,
     com alpha ainda em ~0.5 - a borda inteira aparece reta e so os cantos somem.
     Com closest-side o raio termina no meio de cada borda e a borda toda vai a
     zero; o que sobra visivel e a elipse inscrita.
     Terminar em zero e obrigatorio numa caixa menor que a tela: qualquer alpha
     residual no limite desenharia um retangulo de borda dura no meio da pagina. */
  background: radial-gradient(
    ellipse closest-side at center,
    rgba(6, 6, 6, 0.98) 0%,
    rgba(6, 6, 6, 0.95) calc(var(--veu-alcance) * 0.3),
    rgba(6, 6, 6, 0.78) calc(var(--veu-alcance) * 0.6),
    rgba(6, 6, 6, 0.4) calc(var(--veu-alcance) * 0.85),
    rgba(6, 6, 6, 0) var(--veu-alcance)
  );
}

/* Fica atras do texto sem criar contexto de empilhamento proprio - o canvas das
   particulas mora aqui dentro e precisa passar por cima do fundo escuro. */
.veil__particles {
  position: absolute;
  top: var(--veu-particulas-recuo);
  left: var(--veu-particulas-recuo);
  width: calc(100% - var(--veu-particulas-recuo) * 2);
  height: calc(100% - var(--veu-particulas-recuo) * 2);
  /* Sem a mascara o campo de particulas termina num retangulo duro, mesmo
     recuado. Aqui ela circunscreve: cheio ate metade do raio e dissolvido bem
     antes da borda, entao o efeito lê como um disco e nao como um painel. */
  -webkit-mask-image: radial-gradient(
    ellipse closest-side at center,
    #000 0%,
    #000 50%,
    transparent 88%
  );
  mask-image: radial-gradient(
    ellipse closest-side at center,
    #000 0%,
    #000 50%,
    transparent 88%
  );
}

.veil__card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.veil__label {
  margin-top: 0.75rem;
  font-size: 0.8rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
}

.veil__app {
  font-size: 30px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.8);
}

/* 0.22s tem que bater com VEU_FADE_MS no coreStore: o router espera esse tempo
   antes de trocar a rota, para o destino nao montar por tras de um veu ainda
   translucido. Mudar aqui exige mudar la. */
.veil-fade-enter-active,
.veil-fade-leave-active {
  transition: opacity 0.22s ease;
}
.veil-fade-enter,
.veil-fade-leave-to {
  opacity: 0;
}

/* Quem pediu menos movimento fica so com o fundo escuro e o nome. */
@media (prefers-reduced-motion: reduce) {
  .veil__particles {
    display: none;
  }
}
</style>
